import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { waLink } from "@/data/content";

const inputCls =
  "w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm text-jawa-espresso outline-none focus:border-jawa-turquoise";

const ARRIVAL_OPTIONS = [
  "VIP Box only",
  "VIP Box + airport pickup",
  "VIP Box + airport concierge assistance",
  "Full VIP Welcome Experience (with itinerary / consultation)",
];

// Reusable enquiry modal. Submits to /api/enquiries independently of WhatsApp.
// variant "arrival": VIP arrival quote fields; "service": preferred date + location;
// "tour": tour booking request (dates, party size, pickup preference).
export const EnquiryModal = ({ journey, title, arrival = false, variant, tour, priceNote, onClose, testid = "enquiry-modal" }) => {
  const mode = variant || (arrival ? "arrival" : "default");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    option: ARRIVAL_OPTIONS[0], arrival_date: "", flight_number: "", arrival_airport: "",
    passengers: "", luggage: "", destination: "", assistance: "",
    preferred_date: "", alt_date: "", adults: "", children: "", pickup: "", location: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let details = { phone: form.phone };
      if (mode === "arrival") {
        details = {
          option: form.option, arrival_date: form.arrival_date, flight_number: form.flight_number,
          arrival_airport: form.arrival_airport, passengers: form.passengers, luggage: form.luggage,
          destination: form.destination, requested_assistance: form.assistance, phone: form.phone,
        };
      } else if (mode === "service") {
        details = { phone: form.phone, preferred_date: form.preferred_date, location: form.location };
      } else if (mode === "tour") {
        details = {
          tour: tour?.title, preferred_date: form.preferred_date, alternative_date: form.alt_date,
          adults: form.adults, children: form.children, pickup_preference: form.pickup, phone: form.phone,
        };
      }
      await api.post("/enquiries", { name: form.name, email: form.email, journey, message: form.message, details });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-jawa-nearblack/70 p-4 backdrop-blur-sm" data-testid={testid}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-jawa-stone bg-jawa-ivory p-8">
        {done ? (
          <div className="text-center" data-testid={`${testid}-success`}>
            <p className="font-serif text-2xl font-medium text-jawa-espresso">
              {mode === "tour" ? "Booking request received." : "Enquiry received."}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/65">
              {mode === "tour"
                ? "Thank you — this is a booking request, not yet a confirmed booking. The JÀWÀ team will confirm availability and payment arrangements by email before anything is charged."
                : "Thank you — the JÀWÀ team will confirm availability, exact contents and the total price by email before anything is charged."}
            </p>
            <p className="mt-4 text-xs leading-relaxed text-jawa-espresso/55" data-testid={`${testid}-contact-line`}>
              Your enquiry goes straight to the team at{" "}
              <a href="mailto:info@jawaafrica.com" className="font-semibold text-jawa-turquoise">info@jawaafrica.com</a>.
              Prefer to talk? WhatsApp <span className="font-semibold text-jawa-espresso">+234 915 437 9303</span>.
            </p>
            <a
              href={waLink(`Hello JÀWÀ — urgent assistance please (${title}).`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-urgent-whatsapp`}
              className="mt-4 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise underline"
            >
              Urgent? WhatsApp us instead
            </a>
            <div>
              <button
                onClick={onClose}
                data-testid={`${testid}-close-success`}
                className="mt-6 bg-jawa-espresso px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory hover:bg-jawa-coral"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-coral">{journey}</p>
                <h3 className="mt-1 font-serif text-2xl font-medium text-jawa-espresso">{title}</h3>
                {priceNote && (
                  <p className="mt-2 text-xs font-semibold text-jawa-espresso/70" data-testid={`${testid}-price-note`}>{priceNote}</p>
                )}
                <p className="mt-2 text-xs leading-relaxed text-jawa-espresso/55">
                  {mode === "tour"
                    ? "This is a booking request — we confirm availability and payment arrangements by email before anything is charged."
                    : "This is an enquiry, not a payment — we confirm availability, inclusions and the total price first."}
                </p>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="text-jawa-espresso/50 hover:text-jawa-espresso">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid={`${testid}-name`} className={inputCls} />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" data-testid={`${testid}-email`} className={inputCls} />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp (with country code)" data-testid={`${testid}-phone`} className={inputCls} />
              {mode === "arrival" && (
                <>
                  <select value={form.option} onChange={set("option")} data-testid={`${testid}-option`} className={inputCls}>
                    {ARRIVAL_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="date" value={form.arrival_date} onChange={set("arrival_date")} data-testid={`${testid}-arrival-date`} className={inputCls} aria-label="Arrival date" />
                    <input value={form.flight_number} onChange={set("flight_number")} placeholder="Flight number" data-testid={`${testid}-flight`} className={inputCls} />
                    <input value={form.arrival_airport} onChange={set("arrival_airport")} placeholder="Arrival airport (e.g. LOS)" data-testid={`${testid}-airport`} className={inputCls} />
                    <input type="number" min="1" value={form.passengers} onChange={set("passengers")} placeholder="Passengers" data-testid={`${testid}-passengers`} className={inputCls} />
                  </div>
                  <input value={form.luggage} onChange={set("luggage")} placeholder="Luggage (e.g. 2 large cases + hand luggage)" data-testid={`${testid}-luggage`} className={inputCls} />
                  <input value={form.destination} onChange={set("destination")} placeholder="Destination after arrival (hotel / area)" data-testid={`${testid}-destination`} className={inputCls} />
                  <input value={form.assistance} onChange={set("assistance")} placeholder="Concierge assistance requested (optional)" data-testid={`${testid}-assistance`} className={inputCls} />
                </>
              )}
              {mode === "service" && (
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid={`${testid}-date`} className={inputCls} aria-label="Preferred date" />
                  <input value={form.location} onChange={set("location")} placeholder="Location (e.g. Lagos, UK)" data-testid={`${testid}-location`} className={inputCls} />
                </div>
              )}
              {mode === "tour" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/50">Preferred date</label>
                      <input required type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid={`${testid}-date`} className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/50">Alternative date</label>
                      <input type="date" value={form.alt_date} onChange={set("alt_date")} data-testid={`${testid}-alt-date`} className={inputCls} />
                    </div>
                    <input type="number" min="1" required value={form.adults} onChange={set("adults")} placeholder="Adults" data-testid={`${testid}-adults`} className={inputCls} />
                    <input type="number" min="0" value={form.children} onChange={set("children")} placeholder="Children (if any)" data-testid={`${testid}-children`} className={inputCls} />
                  </div>
                  <input value={form.pickup} onChange={set("pickup")} placeholder="Pickup or meeting preference (hotel pickup / meeting point)" data-testid={`${testid}-pickup`} className={inputCls} />
                </>
              )}
              <textarea
                required
                value={form.message}
                onChange={set("message")}
                placeholder={mode === "tour" ? "Special requirements and anything else we should know" : "Tell us what you need — quantity, sizes, dates, delivery city…"}
                rows={3}
                data-testid={`${testid}-message`}
                className={inputCls}
              />
              <button
                type="submit"
                disabled={busy}
                data-testid={`${testid}-submit`}
                className="flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "tour" ? "Send booking request" : "Send enquiry"}
              </button>
              <a
                href={waLink(`Hello JÀWÀ — urgent assistance please (${title}).`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testid}-urgent-link`}
                className="block text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-jawa-espresso/45 underline transition-colors hover:text-jawa-turquoise"
              >
                Need urgent assistance? Contact us on WhatsApp
              </a>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
